import {
  resourceIdForSupplyShedFeature,
  supplyShedFeatureForResourceId,
} from '@cibo/landmanager/src/features/SupplyShed/Compare/Map/resourceIdUtils'
import {
  CDLLegend,
  GEOPOLITICAL_COUNTY_HOVER_LAYER,
  GEOPOLITICAL_STATE_HOVER_LAYER,
  LayerSwitcher,
  MapGLProps,
  MapGLWithoutViewport,
} from '@cibo/mapgl'
import { MapControlLayout } from '@cibo/mapgl/src/components/MapGL/MapControlLayout'
import { Feature, Geometry } from 'geojson'
import { difference, path, without } from 'ramda'
import { FC, useCallback, useEffect, useState } from 'react'
import {
  MapLayerMouseEvent,
  MapboxGeoJSONFeature,
  NavigationControl,
  useMap,
} from 'react-map-gl/dist/es5'
import { CurrentGeometriesLayer } from './CurrentGeometriesLayer'

interface SelectMapProps extends MapGLProps {
  currentFips: string[]
  onSelectionChanged: (resourceIds: string[]) => void
  isClickable: boolean
  displayOverlay: boolean
  currentFeatures?: Feature<Geometry, { resourceId: string }>[]
}

export const SelectMap: FC<SelectMapProps> = ({
  children,
  currentFips,
  onSelectionChanged,
  isClickable,
  displayOverlay,
  currentFeatures,
  ...props
}) => {
  const [processedIds, setProcessedIds] = useState<string[]>([])
  const map = useMap()
  const [mapReady, setMapReady] = useState(false)

  const selectMapFeature = useCallback(
    (feature: MapboxGeoJSONFeature, select: boolean) => {
      map.default?.setFeatureState(feature, { select })
    },
    [map]
  )

  useEffect(() => {
    if (!mapReady) {
      return
    }

    const newIds = difference(currentFips, processedIds)
    const oldIds = difference(processedIds, currentFips)

    newIds.forEach(resourceId => {
      const feature = supplyShedFeatureForResourceId(resourceId)
      feature && selectMapFeature(feature, true)
    })
    oldIds.forEach(resourceId => {
      const feature = supplyShedFeatureForResourceId(resourceId)
      feature && selectMapFeature(feature, false)
    })

    setProcessedIds(currentFips)
  }, [currentFips, mapReady])

  const handleClick = ({ features }: MapLayerMouseEvent) => {
    const clickedFeature = features && (features.find(path(['state', 'dwell'])) || features[0])

    if (!clickedFeature) {
      return
    }

    const resourceId = resourceIdForSupplyShedFeature({
      feature: clickedFeature,
      type: 'fips',
    })

    if (currentFips.includes(resourceId)) {
      onSelectionChanged(without<string>([resourceId], currentFips))
    } else {
      onSelectionChanged([...currentFips, resourceId])
    }
  }

  return (
    <MapGLWithoutViewport
      {...props}
      interactiveLayerIds={
        !isClickable ? [] : [GEOPOLITICAL_COUNTY_HOVER_LAYER, GEOPOLITICAL_STATE_HOVER_LAYER]
      }
      onClick={handleClick}
      data-testid="select-supply-shed-map"
      onLoad={() => setMapReady(true)}
    >
      <MapControlLayout
        lowerLeft={
          <>
            <LayerSwitcher
              parcels={false}
              sections={false}
              townships={false}
              hucWatersheds={false}
            />
            <CDLLegend />
          </>
        }
        lowerRight={<NavigationControl showZoom position="bottom-right" />}
      />
      {children}
      <CurrentGeometriesLayer currentFips={currentFips} currentFeatures={currentFeatures} />
    </MapGLWithoutViewport>
  )
}
